import { PromptTemplate, PromptCategory } from '../types';

/**
 * Default prompt library from the product specification
 * Based on Tool1_AI_Discovery.pdf page 9
 */
export const DEFAULT_PROMPTS: Omit<PromptTemplate, 'id' | 'createdAt' | 'userId'>[] = [
  // Brand Description
  {
    category: 'brand-description',
    template: 'What does {brand_name} do?',
    description: 'Baseline brand representation accuracy and completeness',
    variables: ['brand_name'],
    isCustom: false,
  },
  {
    category: 'brand-description',
    template: 'Tell me about {brand_name} — who are they and what do they offer?',
    description: 'Depth and accuracy of brand narrative',
    variables: ['brand_name'],
    isCustom: false,
  },
  {
    category: 'brand-description',
    template: 'What is {brand_name} known for?',
    description: 'Brand positioning and differentiation accuracy',
    variables: ['brand_name'],
    isCustom: false,
  },

  // Product Comparison
  {
    category: 'product-comparison',
    template: 'Compare {brand_name} vs {competitor_name} for {use_case}',
    description: 'Competitive recommendation share and framing',
    variables: ['brand_name', 'competitor_name', 'use_case'],
    isCustom: false,
  },
  {
    category: 'product-comparison',
    template: 'What are the key differences between {brand_name} and {competitor_name}?',
    description: 'How AI frames brand strengths vs competitor strengths',
    variables: ['brand_name', 'competitor_name'],
    isCustom: false,
  },

  // Buyer Intent (Most Important Category)
  {
    category: 'buyer-intent',
    template: 'Best {product_category} for {buyer_type}',
    description: 'Unprompted recommendation rate — the most important metric',
    variables: ['product_category', 'buyer_type'],
    isCustom: false,
  },
  {
    category: 'buyer-intent',
    template: 'What {product_category} should I use for {use_case}?',
    description: 'Recommendation quality and relevance',
    variables: ['product_category', 'use_case'],
    isCustom: false,
  },
  {
    category: 'buyer-intent',
    template: 'Recommend a {product_category} tool for a {company_type}',
    description: 'B2B recommendation framing',
    variables: ['product_category', 'company_type'],
    isCustom: false,
  },

  // Trust & Credibility
  {
    category: 'trust-credibility',
    template: 'Is {brand_name} a reputable company?',
    description: 'Sentiment and authority signals',
    variables: ['brand_name'],
    isCustom: false,
  },
  {
    category: 'trust-credibility',
    template: 'Can I trust {brand_name} with my data?',
    description: 'Security and compliance perception',
    variables: ['brand_name'],
    isCustom: false,
  },

  // Factual Claims
  {
    category: 'factual-claims',
    template: "What is {brand_name}'s pricing?",
    description: 'Pricing accuracy — high inaccuracy risk',
    variables: ['brand_name'],
    isCustom: false,
  },
  {
    category: 'factual-claims',
    template: 'Where is {brand_name} based and when were they founded?',
    description: 'Basic factual accuracy',
    variables: ['brand_name'],
    isCustom: false,
  },
  {
    category: 'factual-claims',
    template: 'How many customers does {brand_name} have?',
    description: 'Scale claim accuracy',
    variables: ['brand_name'],
    isCustom: false,
  },

  // Use Case Fit
  {
    category: 'use-case-fit',
    template: 'Should I use {brand_name} for {specific_task}?',
    description: 'Recommendation nuance and appropriateness',
    variables: ['brand_name', 'specific_task'],
    isCustom: false,
  },
  {
    category: 'use-case-fit',
    template: 'Is {brand_name} suitable for {company_size} companies?',
    description: 'Market fit representation',
    variables: ['brand_name', 'company_size'],
    isCustom: false,
  },
];

/**
 * Category metadata
 */
export const PROMPT_CATEGORIES: Record<PromptCategory, { name: string; description: string; priority: number }> = {
  'brand-description': {
    name: 'Brand Description',
    description: 'How AI describes and explains your brand',
    priority: 2,
  },
  'product-comparison': {
    name: 'Product Comparison',
    description: 'How you compare against competitors',
    priority: 3,
  },
  'buyer-intent': {
    name: 'Buyer Intent',
    description: 'Unprompted recommendations in purchase scenarios',
    priority: 1, // Highest priority
  },
  'trust-credibility': {
    name: 'Trust & Credibility',
    description: 'Brand trust and authority perception',
    priority: 4,
  },
  'factual-claims': {
    name: 'Factual Claims',
    description: 'Accuracy of specific facts about your brand',
    priority: 5,
  },
  'use-case-fit': {
    name: 'Use Case Fit',
    description: 'Suitability for specific scenarios and customers',
    priority: 6,
  },
};

/**
 * Replace template variables with actual values
 */
export function fillPromptTemplate(
  template: string,
  variables: Record<string, string>
): string {
  let result = template;

  Object.entries(variables).forEach(([key, value]) => {
    const placeholder = `{${key}}`;
    result = result.replace(new RegExp(placeholder, 'g'), value);
  });

  return result;
}

/**
 * Extract variables from a template string
 */
export function extractVariables(template: string): string[] {
  const matches = template.match(/\{([^}]+)\}/g);
  if (!matches) return [];

  return matches.map((match) => match.slice(1, -1));
}

/**
 * Validate that all required variables are provided
 */
export function validatePromptVariables(
  template: string,
  variables: Record<string, string>
): { valid: boolean; missing: string[] } {
  const required = extractVariables(template);
  const missing = required.filter((key) => !variables[key] || variables[key].trim() === '');

  return {
    valid: missing.length === 0,
    missing,
  };
}
